import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { FormControl, FormHelperText, Stack, TextField, Typography } from '@mui/material';
import { Info } from '@mui/icons-material';
import { ComboBox, LegalEntityIdField, VATField, AddressComboBox } from '@fnol/library/src/components';
import { personType, translations } from '../../lib/consts';
import { GenericObject, question50MaxLength, question10MaxLength, questionMaxLength } from '@fnol/library/src/lib';
import { prepareStreetNames } from '../../lib/helpers';
import { getStreetNames } from '../../rpc/actions';
import { useApi } from '@fnol/library/src/hooks';

type Props = {
    legalEntityType: personType;
};

const LegalEntity = ({ legalEntityType }: Props) => {
    const [isFirstPass, setIsFirstPass] = useState(true);

    const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<any>();
    const name = `${personType[legalEntityType]}LegalEntityName`;
    const id = `${personType[legalEntityType]}LegalEntityID`;
    const vat = `${personType[legalEntityType]}LegalEntityVAT`;
    const country = `${personType[legalEntityType]}LegalEntityCountry`;
    const countryText = `${personType[legalEntityType]}LegalEntityMyCountry`;
    const cityInSerbia = `${personType[legalEntityType]}LegalEntityCityInSerbia`;
    const address = `${personType[legalEntityType]}LegalEntityAddress`;
    const addressNumber = `${personType[legalEntityType]}LegalEntityAddressNumber`;
    const addressApartment = `${personType[legalEntityType]}LegalEntityAddressApartment`;
    const email = `${personType[legalEntityType]}LegalEntityEmail`;
    const duplicatedId = `${personType[legalEntityType]}DuplicatedId`;

    let cityCodeForStreetSearch = '';
    if (values[cityInSerbia] && values[cityInSerbia] !== '' && !!values[cityInSerbia].code)
        cityCodeForStreetSearch = values[cityInSerbia].code;

    const saveStreetNames = (res: GenericObject) => {
        const streetNames = prepareStreetNames(res.data);
        setFieldValue('streetNames', streetNames);
    };
    const { response: streetNames, fetchMethod: fetchStreetNames } = useApi(
        () => getStreetNames(cityCodeForStreetSearch),
        saveStreetNames,
    );

    // selected country textual representation
    const selectedLegalEntityCountry = values.preparedCountries?.find(
        (item: any) => item.id === parseInt(values[country] as string),
    );
    values[countryText] = selectedLegalEntityCountry?.text;

    const checkIfIdExists = () => {
        const insertTypes: number[] = [1, 2, 3, 4, 5, 6];
        const insertedIds: string[] = [];

        insertTypes.map((insertType) => {
            insertedIds.push(values[`${personType[insertType]}LegalEntityID`]);
        });

        const duplicatedIdExists = insertedIds.some((idValue) => {
            if (idValue && idValue !== '') {
                return insertedIds.indexOf(idValue) !== insertedIds.lastIndexOf(idValue);
            }
        });

        setFieldValue(duplicatedId, duplicatedIdExists);
    };

    useEffect(() => {
        if (isFirstPass) {
            setIsFirstPass(false);
        } else if (values[cityInSerbia] && values[cityInSerbia] !== '' && !!values[cityInSerbia].code) {
            setFieldValue(address, '');
            fetchStreetNames();
        }
    }, [values[cityInSerbia]]);

    return (
        <>
            <FormControl>
                <Field
                    name={name}
                    id={name}
                    label={translations.legalInfo.SUBMITTER_LEGAL_NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[name] && errors[name])}
                    required
                />
                {touched[name] && errors[name] ? (
                    <FormHelperText error={true}>
                        <>{errors[name]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl error={!!(touched[cityInSerbia] && errors[cityInSerbia])}>
                <Field
                    name={cityInSerbia}
                    id={cityInSerbia}
                    label={translations.CITY}
                    options={
                        values.cities || [
                            {
                                id: '',
                                value: '',
                                text: '',
                                code: '',
                            },
                        ]
                    }
                    onChange={handleChange}
                    defaultValue={values[cityInSerbia]}
                    as={ComboBox}
                    error={!!(touched[cityInSerbia] && errors[cityInSerbia])}
                    required
                />
                {touched[cityInSerbia] && errors[cityInSerbia] ? (
                    <FormHelperText error={true}>
                        <>{errors[cityInSerbia]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={address}
                    id={address}
                    label={translations.ADDRESS}
                    options={
                        values.streetNames || [
                            {
                                id: '',
                                value: '',
                                label: '',
                            },
                        ]
                    }
                    defaultValue={values[address]}
                    as={AddressComboBox}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[address] && errors[address])}
                    required
                />
                {touched[address] && errors[address] ? (
                    <FormHelperText error={true}>
                        <>{errors[address]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={addressNumber}
                    id={addressNumber}
                    label={translations.ADDRESS_NO}
                    as={TextField}
                    inputProps={{
                        maxLength: question10MaxLength,
                    }}
                    error={!!(touched[addressNumber] && errors[addressNumber])}
                    required
                />
                {touched[addressNumber] && errors[addressNumber] ? (
                    <FormHelperText error={true}>
                        <>{errors[addressNumber]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={addressApartment}
                    id={addressApartment}
                    label={translations.APARTMENT_NO}
                    as={TextField}
                    inputProps={{
                        maxLength: question10MaxLength,
                    }}
                />
            </FormControl>

            <FormControl>
                <Field
                    name={id}
                    id={id}
                    label={translations.legalInfo.ID}
                    as={LegalEntityIdField}
                    onBlur={checkIfIdExists}
                    selectedCountry={values[country]}
                    error={!!(touched[id] && errors[id])}
                    required
                />
                {touched[id] && errors[id] ? (
                    <FormHelperText error={true}>
                        <>{errors[id]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            {values[duplicatedId] ? (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5} id={duplicatedId}>
                        {translations.validationMessages.ALREADY_HAS_IMMATERIAL}
                    </Typography>
                </Stack>
            ) : null}

            <FormControl>
                <Field
                    name={vat}
                    id={vat}
                    label={translations.legalInfo.VAT}
                    as={VATField}
                    selectedCountry={values[country]}
                    error={!!(touched[vat] && errors[vat])}
                    required
                />
                {touched[vat] && errors[vat] ? (
                    <FormHelperText error={true}>
                        <>{errors[vat]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={email}
                    id={email}
                    label={translations.legalInfo.EMAIL}
                    type="email"
                    as={TextField}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    error={!!(touched[email] && errors[email])}
                    required
                />
                {touched[email] && errors[email] ? (
                    <FormHelperText error={true}>
                        <>{errors[email]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
        </>
    );
};

export const LegalEntityCleanup = (
    type: personType,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
) => {
    setFieldValue(`${personType[type]}LegalEntityName`, '');
    setFieldValue(`${personType[type]}LegalEntityID`, '');
    setFieldValue(`${personType[type]}LegalEntityVAT`, '');
    setFieldValue(`${personType[type]}LegalEntityCityInSerbia`, '');
    setFieldValue(`${personType[type]}LegalEntityAddress`, '');
    setFieldValue(`${personType[type]}LegalEntityAddressNumber`, '');
    setFieldValue(`${personType[type]}LegalEntityAddressApartment`, '');
    setFieldValue(`${personType[type]}LegalEntityEmail`, '');
    setFieldValue(`${personType[type]}DuplicatedId`, '');
};

export default LegalEntity;
